/* jshint esversion: 6 */
/***********************************************************************
 Factory to create a specific course
 ***********************************************************************/
import {CourseFacade} from "api/CourseFacade";
import {LocalStorageCourse} from "localstorage/LocalStorageCourse";
import {PublisherCourseFactory} from "publisher/PublisherCourseFactory";
import {ScormCourseFactory} from "scorm/ScormCourseFactory";
import {ScormType} from "scorm/common/ScormType";
import {ScormApiDetector} from "scorm/common/ScormApiDetector";
import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";
import {AiccCourseFactory} from "../aicc/AiccCourseFactory";

export function CourseFactory() {
    "use strict";

    const logId = "CourseFactory";

    function createCourse(builder) {

        Assert.checkArgumentType(builder, "function");

        let course;

        try {
            course = builder();
        } catch (error) {
            LoggingService.error(`...error while creating course: ${error}`, logId);
            return null;
        }

        LoggingService.log("...course successfully created!", logId);

        return course ? new CourseFacade(course) : null;
    }

    function getScormType(options) {

        if (!options || !options.sScoExportVersion) {
            return ScormType.SCORM_1_2;
        }

        switch (options.sScoExportVersion) {
            case "1.3":
            case "1_3":
            case "2004":
                return ScormType.SCORM_1_3;
            default:
                return ScormType.SCORM_1_2;
        }
    }

    function isTtsApi(api) {
        return api && typeof (api.TTIsDelegateApi) === "function" && api.TTIsDelegateApi();
    }

    function setTtsApiAsGlobalApi(api) {

        if (api.TTGetApiType() === ScormType.SCORM_1_3) {
            window.API_1484_11 = api;
        }

        window.API = api;
    }

    // revealing public api
    return {

        createCookieCourse: function (userId, courseOptions) {
            LoggingService.log("creating course for localstorage or cookie environment...", logId);
            return createCourse(() => {
                Assert.checkArgument(userId);
                return new LocalStorageCourse(userId, courseOptions);
            });
        },

        createAcceleratorCourse: function (lmsQuery, courseOptions) {
            LoggingService.log("creating course for publisher with login...", logId);
            return createCourse(() => {
                Assert.checkArgument(lmsQuery);
                Assert.checkArgument( courseOptions );
                Assert.checkArgument( courseOptions.sCourseCompactId );
                Assert.checkArgument( courseOptions.sCourseLanguage );
                return PublisherCourseFactory.createCourse(
                     lmsQuery,
                     courseOptions.sCourseCompactId,
                     courseOptions.sCourseLanguage,
                     courseOptions.nMasteryScore
                 );
            });
        },

        createScormCourse: function (lmsApi, scormOptions) {
            LoggingService.log("creating course for scorm lms...", logId);
            return createCourse(() => {

                if (isTtsApi(lmsApi)) {
                    setTtsApiAsGlobalApi(lmsApi);
                    return lmsApi;
                }

                Assert.checkArgument(scormOptions);

                const scormType = getScormType(scormOptions),
                    masteryScore = scormOptions ? scormOptions.nMasteryScore : null;

                LoggingService.log(`lms target version: ${scormType}` === ScormType.SCORM_1_3 ? "1.3" : "1.2");

                if (lmsApi === null) {
                    const apiDetector = new ScormApiDetector();
                    lmsApi = apiDetector.lookupScormApi(scormType);
                    if (lmsApi === null) {
                        LoggingService.log("lms scorm api not found!", logId);
                        return null;
                    }
                }

                return ScormCourseFactory.createCourse(lmsApi, scormType, masteryScore);
            });
        },

        createAiccCourse: function ( aiccOptions, scormOptions, courseOptions ) {
            LoggingService.log("creating course for aicc lms...", logId);
            return createCourse(() => {

                Assert.checkArgument(aiccOptions);
                Assert.checkArgument(courseOptions);

                const options =  {
                    aiccUrl: aiccOptions.sAiccURL,
                    aiccSID: aiccOptions.sAiccSID,
                    aiccProxy: aiccOptions.sAiccProxy,
                    courseId: courseOptions.sCourseCompactId,
                    courseLanguage: courseOptions.sCourseLanguage,
                    courseMastersScore: courseOptions.nMasteryScore
                }
                return AiccCourseFactory.createCourse(options);
            });
        }
    };
}
